import { useEffect, useRef, useState } from 'react';

/**
 * Google Identity Services button.
 *
 * Renders nothing unless the server reports Google sign-in is configured, so
 * the app is fully usable on email/password alone and nothing half-works when
 * GOOGLE_CLIENT_ID is unset.
 *
 * Google's script requires a secure context, so this does not appear over
 * plain http on an IP address — same constraint as the service worker and Web
 * Share. On localhost it works, because browsers treat localhost as secure.
 */
const SRC = 'https://accounts.google.com/gsi/client';

function loadScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  const existing = document.querySelector(`script[src="${SRC}"]`);
  if (existing) return new Promise((res, rej) => {
    existing.addEventListener('load', res);
    existing.addEventListener('error', rej);
  });
  return new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = SRC; s.async = true; s.defer = true;
    s.onload = res; s.onerror = () => rej(new Error('could not load Google sign-in'));
    document.head.appendChild(s);
  });
}

export default function GoogleButton({ clientId, onCredential, disabled }) {
  const slot = useRef(null);
  const [failed, setFailed] = useState(false);
  // onCredential is recreated each render; a ref keeps the callback Google
  // holds pointing at the current one without re-initialising the button.
  const cb = useRef(onCredential);
  cb.current = onCredential;

  useEffect(() => {
    if (!clientId || !slot.current) return;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !slot.current) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (resp) => cb.current?.(resp.credential),
          // Leave auto-select off: silently signing someone back in after they
          // chose to sign out is surprising, and on a shared crew phone it
          // could put the wrong person's account on screen.
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        window.google.accounts.id.renderButton(slot.current, {
          theme: 'outline', size: 'large', width: 320,
          text: 'signin_with', shape: 'rectangular', logo_alignment: 'left',
        });
      })
      .catch(() => { if (!cancelled) setFailed(true); });

    return () => { cancelled = true; };
  }, [clientId]);

  if (!clientId) return null;
  if (failed) {
    return (
      <p className="sb" style={{ textAlign: 'center' }}>
        Google sign-in couldn’t load. Use your email and password below.
      </p>
    );
  }

  return (
    <div
      className="google-slot"
      aria-busy={disabled ? 'true' : undefined}
      style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
    >
      <div ref={slot} />
    </div>
  );
}
